import {Radio, Select, TextInput} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import React, {useEffect, useMemo} from 'react';
import type {
  NotificationRuleMetadata,
  NotificationRuleResponse,
} from 'src/api/ProactApiClient';
import {
  FormStateEnum,
  ModuleEnum,
  NotificationRuleChangeTypeEnum,
  NotificationRuleCourseChangeTypeEnum,
  NotificationRuleTypeEnum,
} from 'src/api/ProactApiClient';
import {SelectableCard} from 'src/design/molecules/SelectableCard';
import {ProjectSelectBox} from 'src/app/databound-elements/ProjectSelectBox';
import {
  useGetCourseDefinitionContentSelectOptionsQuery,
  useGetCourseDefinitionSelectOptionsQuery,
} from 'src/redux/queries/courseDefinition';
import {useGetFormDefinitionSelectOptionsQuery} from 'src/redux/queries/formDefinition';
import {useGetAttachmentClassificationListQuery} from 'src/redux/queries/attachmentClassification';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import type {ISelectOption} from 'src/types/Form';
import {CourseChangeTypeOptions} from '../notificationRuleHelpers';
import {attachmentModuleEnumOptions} from 'src/app/attachments/attachmentModuleEnumOptions';
import type {NotificationRuleValidationErrors} from '../validation/notificationRuleSchema';
import {EditNotificationRuleContent} from './EditNotificationRuleContent';
import {EditNotificationRules} from './EditNotificationRules';
import {EditNotificationTargets} from './EditNotificationTargets';

export interface EditNotificationRuleFormProps {
  data: NotificationRuleResponse;
  onDataChange(data: NotificationRuleResponse): void;
  mode: 'edit' | 'view';
  validationErrors?: NotificationRuleValidationErrors;
  onFieldChange?: (field: keyof NotificationRuleValidationErrors) => void;
}
export const EditNotificationRuleForm: React.FC<
  EditNotificationRuleFormProps
> = ({data, onDataChange, mode, validationErrors, onFieldChange}) => {
  const disabled = mode === 'view';
  const [moduleOptions, setModuleOptions] = React.useState<
    {id: number; label: string; description: string}[]
  >([]);

  const {skipForms, skipTraining} = useSkipTokensByFeature();

  const {data: forms} = useGetFormDefinitionSelectOptionsQuery(
    {start: 0, length: 1000, active: true, includeSubforms: true},
    {skip: skipForms}
  );
  const {data: courses} = useGetCourseDefinitionSelectOptionsQuery(
    {start: 0, length: 1000, active: true},
    {
      skip:
        skipTraining || Number(data.metadata.module) !== ModuleEnum.Training,
    }
  );
  const {data: courseContents} =
    useGetCourseDefinitionContentSelectOptionsQuery(
      {
        start: 0,
        length: 1000,
        courseDefinitionId: data.metadata.courseDefinitionId,
      },
      {
        skip:
          skipTraining ||
          Number(data.metadata.module) !== ModuleEnum.Training ||
          data.metadata.courseDefinitionId === undefined,
      }
    );

  const isAttachmentChangeEvent =
    Number(data.ruleType) === NotificationRuleTypeEnum.ChangeEvent &&
    Number(data.metadata.module) === ModuleEnum.Attachments;

  const isAttachmentModule =
    Number(data.metadata.module) === ModuleEnum.Attachments;

  const {data: attachmentClassifications} =
    useGetAttachmentClassificationListQuery(
      {start: 0, length: 1000},
      {skip: !isAttachmentModule}
    );

  const attachmentClassificationOptions = useMemo(
    () =>
      (attachmentClassifications?.data ?? []).map(c => ({
        value: String(c.id),
        label: c.name,
      })),
    [attachmentClassifications]
  );

  useEffect(() => {
    const type: NotificationRuleTypeEnum = Number(data.ruleType);
    switch (type) {
      case NotificationRuleTypeEnum.Reminder:
        setModuleOptions([
          {
            id: ModuleEnum.Actions,
            label: 'Actions',
            description: 'Monitor action assignments and due dates',
          },
          {
            id: ModuleEnum.Approvals,
            label: 'Approvals',
            description: 'Monitor approval assignments and due dates',
          },
          {
            id: ModuleEnum.AssignedForms,
            label: 'Assigned Forms',
            description: 'Monitor assigned form due dates',
          },
          {
            id: ModuleEnum.Attachments,
            label: 'Attachments',
            description: 'Monitor attachment expiration dates',
          },
          {
            id: ModuleEnum.Forms,
            label: 'Forms',
            description: 'Monitor form status durations',
          },
          {
            id: ModuleEnum.ToolboxTalks,
            label: 'Toolbox Talks',
            description: 'Monitor toolbox talk due dates',
          },
          {
            id: ModuleEnum.Training,
            label: 'Training',
            description: 'Monitor training due dates and expirations',
          },
        ]);
        break;

      case NotificationRuleTypeEnum.ChangeEvent:
        setModuleOptions([
          {
            id: ModuleEnum.Actions,
            label: 'Actions',
            description: 'Monitor action assignments and status changes',
          },
          {
            id: ModuleEnum.Approvals,
            label: 'Approvals',
            description: 'Monitor approval assignments and status changes',
          },
          {
            id: ModuleEnum.AssignedForms,
            label: 'Assigned Forms',
            description: 'Monitor assigned form status changes',
          },
          {
            id: ModuleEnum.ToolboxTalks,
            label: 'Toolbox Talks',
            description: 'Monitor toolbox talk status changes',
          },
          {
            id: ModuleEnum.Forms,
            label: 'Forms',
            description: 'Monitor form status and field value changes',
          },
          {
            id: ModuleEnum.Training,
            label: 'Training',
            description: 'Monitor course enrollment and completion',
          },
          {
            id: ModuleEnum.ItemTrackerAssignments,
            label: 'Item Tracker',
            description: 'Monitor item assignment status changes',
          },
          {
            id: ModuleEnum.SafetyEvent,
            label: 'Safety Event',
            description: 'Monitor safety event status changes',
          },
          {
            id: ModuleEnum.Personnel,
            label: 'Personnel',
            description: 'Monitor personnel record changes',
          },
          {
            id: ModuleEnum.Attachments,
            label: 'Attachments',
            description: 'Monitor attachment creation and updates',
          },
          {
            id: ModuleEnum.Inspections,
            label: 'Inspections',
            description: 'Monitor inspection status and assignee changes',
          },
        ]);

        break;
      default:
        console.warn('Found unexpected rule type: ', data.ruleType);
        throw 'Invalid Rule Type!';
    }
  }, [data.ruleType]);

  function handleNameChange(value: string): void {
    onFieldChange?.('name');
    onDataChange({
      ...data,
      name: value,
    });
  }

  function handleActiveChange(checked: boolean): void {
    onDataChange({
      ...data,
      active: checked,
    });
  }

  function handleProjectChange(option: ISelectOption | null): void {
    const projectId = option?.id === '' ? undefined : Number(option?.id);
    onDataChange({
      ...data,
      projectId: projectId,
    });
  }

  function handleMetadataChange(metadata: NotificationRuleMetadata): void {
    onDataChange({
      ...data,
      metadata: metadata,
    });
  }

  // Fields that should be reset when switching rule type or module
  const resetConditionsAndTargets: Partial<NotificationRuleMetadata> = {
    reminderConditions: [],
    formChangeConditions: [],
    personnelChangeConditions: [],
    taskAssignmentChangeConditions: [],
    itemAssignmentChangeConditions: [],
    safetyEventChangeConditions: [],
    inspectionChangeConditions: [],
    courseSessionChangeConditions: [],
    courseContentChangeConditions: [],
    attachmentChangeConditions: [],
    formFieldPersonnelTargets: [],
    formFieldInvariantIds: [],
    notificationVariableFormFieldInvariantIds: [],
    formDefinitionId: undefined,
    formStateFilter: undefined,
    courseDefinitionId: undefined,
    courseDefinitionContentInvariantId: undefined,
    courseChangeType: undefined,
  };

  function handleRuleTypeChange(value: string): void {
    onDataChange({
      ...data,
      ruleType: Number(value),
      metadata: {
        ...data.metadata,
        ...resetConditionsAndTargets,
        module: ModuleEnum.Actions,
        changeType: NotificationRuleChangeTypeEnum.TaskAssignmentChange,
      },
    });
  }

  function handleModuleChange(value: string): void {
    const moduleValue = Number(value);
    let changeType = NotificationRuleChangeTypeEnum.Invalid;
    if (moduleValue === ModuleEnum.Forms) {
      changeType = NotificationRuleChangeTypeEnum.FormChange;
    }
    if (moduleValue === ModuleEnum.Training) {
      changeType = NotificationRuleChangeTypeEnum.CourseChange;
    }
    if (
      moduleValue === ModuleEnum.AssignedForms ||
      moduleValue === ModuleEnum.ToolboxTalks ||
      moduleValue === ModuleEnum.Actions ||
      moduleValue === ModuleEnum.Approvals
    ) {
      changeType = NotificationRuleChangeTypeEnum.TaskAssignmentChange;
    }
    if (moduleValue === ModuleEnum.SafetyEvent) {
      changeType = NotificationRuleChangeTypeEnum.SafetyEventChange;
    }
    if (moduleValue === ModuleEnum.ItemTrackerAssignments) {
      changeType = NotificationRuleChangeTypeEnum.ItemAssignmentChange;
    }
    if (moduleValue === ModuleEnum.Personnel) {
      changeType = NotificationRuleChangeTypeEnum.PersonnelChange;
    }
    if (moduleValue === ModuleEnum.Attachments) {
      changeType = NotificationRuleChangeTypeEnum.AttachmentCreated;
    }
    if (moduleValue === ModuleEnum.Inspections) {
      changeType = NotificationRuleChangeTypeEnum.InspectionChange;
    }

    onDataChange({
      ...data,
      metadata: {
        ...data.metadata,
        ...resetConditionsAndTargets,
        module: moduleValue,
        changeType: changeType,
      },
    });
  }

  function handleFormChange(value: string | null): void {
    onFieldChange?.('formDefinitionId');
    onDataChange({
      ...data,
      metadata: {
        ...data.metadata,
        formDefinitionId: value ? Number(value) : undefined,
      },
    });
  }

  function handleFormStateFilterChange(value: string | null): void {
    onDataChange({
      ...data,
      metadata: {
        ...data.metadata,
        formStateFilter: value ? (Number(value) as FormStateEnum) : undefined,
      },
    });
  }

  function handleCourseChangeTypeChange(value: string): void {
    onDataChange({
      ...data,
      metadata: {
        ...data.metadata,
        courseChangeType: Number(value),
      },
    });
  }

  function handleCourseChange(value: string | null): void {
    onFieldChange?.('courseDefinitionId');
    onDataChange({
      ...data,
      metadata: {
        ...data.metadata,
        courseDefinitionId: value ? Number(value) : undefined,
      },
    });
  }

  function handleCourseContentChange(value: string | null): void {
    onFieldChange?.('courseDefinitionContentInvariantId');
    onDataChange({
      ...data,
      metadata: {
        ...data.metadata,
        courseDefinitionContentInvariantId: value ?? undefined,
      },
    });
  }

  // Convert API SelectOption[] to Mantine Select data format
  const formSelectData = (forms?.data ?? []).map(opt => ({
    value: String(opt.id),
    label: opt.label ?? String(opt.id),
  }));

  const courseSelectData = (courses?.data ?? []).map(opt => ({
    value: String(opt.id),
    label: opt.label ?? String(opt.id),
  }));

  const courseContentSelectData = (courseContents?.data ?? []).map(opt => ({
    value: String(opt.id),
    label: opt.label ?? String(opt.id),
  }));

  return (
    <Stack mb="lg">
      <TextInput
        label="Name"
        value={data.name ?? ''}
        onChange={e => handleNameChange(e.currentTarget.value)}
        disabled={disabled}
        error={validationErrors?.name}
        required
        withAsterisk
        maxLength={250}
      />

      <Switch
        label={
          <Group gap="xs">
            <Text size="sm">Active</Text>
            <Text size="sm" c={data.active ? 'green.8' : 'red.8'}>
              {data.active ? 'Yes' : 'No'}
            </Text>
          </Group>
        }
        color="green.8"
        description="Enable or disable the notification rule. If disabled, the rule will not trigger any notifications."
        checked={data.active}
        onChange={e => handleActiveChange(e.currentTarget.checked)}
        disabled={disabled}
      />

      <Box maw="400px">
        <ProjectSelectBox
          label="Project"
          value={data.projectId ? String(data.projectId) : ''}
          onSelectChangeCallback={handleProjectChange}
          disabled={disabled}
        />
      </Box>

      <Stack gap="xs" mb="md">
        <Title order={4}>Notification Type</Title>
        <Text size="sm" c="dimmed">
          Choose how this notification is triggered.
        </Text>
        <SimpleGrid cols={{base: 1, sm: 2}}>
          <SelectableCard
            label="Reminder"
            description="Send reminders once per day based on date conditions like due dates and expirations."
            value={String(NotificationRuleTypeEnum.Reminder)}
            selected={
              Number(data.ruleType) === NotificationRuleTypeEnum.Reminder
            }
            onClick={handleRuleTypeChange}
            disabled={disabled}
          />
          <SelectableCard
            label="Change Event"
            description="Send notifications as changes occur in the system, like status updates or record modifications."
            value={String(NotificationRuleTypeEnum.ChangeEvent)}
            selected={
              Number(data.ruleType) === NotificationRuleTypeEnum.ChangeEvent
            }
            onClick={handleRuleTypeChange}
            disabled={disabled}
          />
        </SimpleGrid>
      </Stack>

      <Stack gap="xs" mb="md">
        <Title order={4}>Target Module</Title>
        <Text size="sm" c="dimmed">
          The module determines which conditions and variables are available.
        </Text>
        <SimpleGrid cols={{base: 1, sm: 2, lg: 3}}>
          {moduleOptions.map(option => (
            <SelectableCard
              key={option.id}
              label={option.label}
              description={option.description}
              value={String(option.id)}
              selected={Number(data.metadata.module) === option.id}
              onClick={handleModuleChange}
              disabled={disabled}
            />
          ))}
        </SimpleGrid>
      </Stack>

      {Number(data.metadata.module) === ModuleEnum.Forms && (
        <Paper withBorder p="md" bg="var(--mantine-color-gray-0)">
          <Stack gap="md">
            <Select
              label="Which form do you want to target?"
              description={
                Number(data.ruleType) === NotificationRuleTypeEnum.Reminder
                  ? 'Leave blank to target all forms.'
                  : undefined
              }
              data={formSelectData}
              value={
                data.metadata.formDefinitionId
                  ? String(data.metadata.formDefinitionId)
                  : null
              }
              onChange={handleFormChange}
              disabled={disabled}
              error={validationErrors?.formDefinitionId}
              clearable
              searchable
              withAsterisk={
                Number(data.ruleType) !== NotificationRuleTypeEnum.Reminder
              }
              maw={400}
            />

            {Number(data.ruleType) === NotificationRuleTypeEnum.Reminder && (
              <Select
                label="Which form status do you want to target?"
                description="Leave blank to target forms in any status."
                data={[
                  {value: String(FormStateEnum.Draft), label: 'Draft'},
                  {value: String(FormStateEnum.Published), label: 'Published'},
                ]}
                value={
                  data.metadata.formStateFilter != null
                    ? String(data.metadata.formStateFilter)
                    : null
                }
                onChange={handleFormStateFilterChange}
                disabled={disabled}
                clearable
                maw={400}
              />
            )}
          </Stack>
        </Paper>
      )}

      {Number(data.metadata.module) === ModuleEnum.Training &&
        Number(data.ruleType) === NotificationRuleTypeEnum.ChangeEvent &&
        Number(data.metadata.changeType) ===
          NotificationRuleChangeTypeEnum.CourseChange && (
          <Paper withBorder p="md" bg="var(--mantine-color-gray-0)">
            <Stack gap="md">
              <Radio.Group
                label="Which type of course data do you want to target?"
                description="The course data you select determines which conditions and variables you can use in your notification, as well if selecting a course is required or not."
                value={String(data.metadata.courseChangeType)}
                onChange={handleCourseChangeTypeChange}
                withAsterisk>
                <Group mt="xs">
                  {CourseChangeTypeOptions.map(option => (
                    <Radio
                      key={option.id}
                      value={String(option.id)}
                      label={option.label}
                      disabled={disabled}
                    />
                  ))}
                </Group>
              </Radio.Group>

              <Select
                label="Which course do you want to target?"
                withAsterisk
                description={
                  Number(data.metadata.courseChangeType) ===
                    NotificationRuleCourseChangeTypeEnum.CourseSessionChange ||
                  Number(data.metadata.courseChangeType) ===
                    NotificationRuleCourseChangeTypeEnum.CourseAssignmentChange
                    ? 'Leave blank to target all courses.'
                    : undefined
                }
                data={courseSelectData}
                value={
                  data.metadata.courseDefinitionId
                    ? String(data.metadata.courseDefinitionId)
                    : null
                }
                onChange={handleCourseChange}
                disabled={disabled}
                error={validationErrors?.courseDefinitionId}
                clearable
                searchable
                maw={400}
              />

              {Number(data.metadata.courseChangeType) ===
                NotificationRuleCourseChangeTypeEnum.CourseContentChange && (
                <Select
                  label="Which course content do you want to target for this course?"
                  data={courseContentSelectData}
                  value={
                    data.metadata.courseDefinitionContentInvariantId ?? null
                  }
                  onChange={handleCourseContentChange}
                  disabled={disabled}
                  error={validationErrors?.courseDefinitionContentInvariantId}
                  clearable
                  searchable
                  withAsterisk
                  maw={400}
                />
              )}
            </Stack>
          </Paper>
        )}

      {isAttachmentChangeEvent && (
        <Paper withBorder p="md" bg="var(--mantine-color-gray-0)">
          <Stack gap="md">
            <Radio.Group
              label="Which attachment event do you want to target?"
              description="Select whether to trigger on new attachments or updates to existing attachments."
              value={String(data.metadata.changeType)}
              onChange={value =>
                handleMetadataChange({
                  ...data.metadata,
                  attachmentChangeConditions: [],
                  changeType: Number(value) as NotificationRuleChangeTypeEnum,
                })
              }
              withAsterisk>
              <Group mt="xs">
                <Radio
                  value={String(
                    NotificationRuleChangeTypeEnum.AttachmentCreated
                  )}
                  label="Attachment Created"
                  disabled={disabled}
                />
                <Radio
                  value={String(
                    NotificationRuleChangeTypeEnum.AttachmentUpdated
                  )}
                  label="Attachment Updated"
                  disabled={disabled}
                />
              </Group>
            </Radio.Group>
          </Stack>
        </Paper>
      )}

      <Divider />
      <EditNotificationTargets
        data={data.metadata}
        onDataChange={handleMetadataChange}
        disabled={disabled}
        validationError={validationErrors?.recipients}
        onRecipientsChange={() => onFieldChange?.('recipients')}
        formDefinitionId={data.metadata.formDefinitionId ?? undefined}
      />
      <Divider />
      <EditNotificationRules
        ruleType={data.ruleType}
        data={data.metadata}
        onDataChange={handleMetadataChange}
        disabled={disabled}
        validationError={validationErrors?.conditions}
        onConditionsChange={() => onFieldChange?.('conditions')}
        attachmentClassificationOptions={attachmentClassificationOptions}
        attachmentTypeOptions={attachmentModuleEnumOptions}
      />
      <Divider />
      <EditNotificationRuleContent
        data={data}
        onDataChange={onDataChange}
        disabled={disabled}
        subjectError={validationErrors?.subject}
        bodyError={validationErrors?.body}
        onSubjectChange={() => onFieldChange?.('subject')}
        onBodyChange={() => onFieldChange?.('body')}
      />
    </Stack>
  );
};
